import { GraphqlContextValues } from '@sbu/contexts.graphql';

import { ContentType, SearchResultType } from 'core/graphql';

import {
  booleanFieldDefaults,
  FieldTypes,
  keyValueFieldDefaults,
} from '../types/fieldTypes';
import {
  dateOperators,
  dateRangeOperators,
  globalOperators,
  numericOperators,
  stringOperators,
  textOperators,
} from '../types/operatorTypes';
import { getEntityTypeFilters } from '../utils';
import { autocompleteQuery } from './gql';

export const getSignalFields = (
  graphqlContext: GraphqlContextValues,
  canSearchCanaries = false
): FieldTypes[] => [
  {
    label: 'Event Time',
    prop: 'timestamp',
    operators: [
      ...globalOperators,
      ...dateRangeOperators.filter((d) => d.type !== 'next'),
      ...dateOperators,
    ],
    type: 'date',
    source: 'signal',
    disableFuture: true,
  },
  {
    label: 'Created Time',
    prop: 'created',
    operators: [
      ...globalOperators,
      ...dateRangeOperators.filter((d) => d.type !== 'next'),
      ...dateOperators,
    ],
    type: 'date',
    source: 'signal',
    disableFuture: true,
  },
  {
    label: 'Content Type',
    prop: 'contentType',
    operators: [...globalOperators],
    type: 'list',
    source: 'signal',
    options: [
      {
        label: 'Anomaly',
        prop: ContentType.Anomaly,
      },
      {
        label: 'Rule',
        prop: ContentType.Rule,
      },
      {
        label: 'Threat Intelligence',
        prop: ContentType.ThreatIntel,
      },
      {
        label: 'File Analysis',
        prop: ContentType.FileAnalysis,
      },
    ],
  },
  {
    label: 'Name',
    prop: 'name',
    query: (value) =>
      autocompleteQuery(
        SearchResultType.Signals,
        'name',
        value,
        graphqlContext
      ),
    operators: [...globalOperators, ...textOperators, ...stringOperators],
    type: 'text',
    source: 'signal',
  },
  {
    ...keyValueFieldDefaults,
    label: 'Tags',
    prop: 'tag',
    source: 'signal',
  },
  {
    label: 'Severity',
    prop: 'severity',
    operators: [...globalOperators, ...numericOperators],
    type: 'numeric',
    source: 'signal',
    min: 0,
  },
  {
    ...booleanFieldDefaults,
    label: 'Suppressed',
    prop: 'suppressed',
    source: 'signal',
  },
  {
    ...booleanFieldDefaults,
    label: 'Prototype Rules',
    prop: 'prototype',
    source: 'signal',
  },
  {
    label: 'Rule ID',
    prop: 'ruleId',
    operators: [...globalOperators],
    type: 'text',
    source: 'signal',
  },
  {
    label: 'Rule Type',
    prop: 'ruleType',
    operators: [...globalOperators],
    options: [
      ...[
        {
          label: 'Aggregation',
          prop: 'AGGREGATION',
        },
        {
          label: 'Chain',
          prop: 'CHAIN',
        },
        {
          label: 'First Seen',
          prop: 'FIRST',
        },
        {
          label: 'Match',
          prop: 'MATCH',
        },
        {
          label: 'Threshold',
          prop: 'THRESHOLD',
        },
        {
          label: 'Outlier',
          prop: 'OUTLIER',
        },
      ],
    ],
    type: 'list',
    source: 'signal',
  },
  {
    label: 'Sensor Zone (Record)',
    prop: 'sensorZone',
    operators: [...globalOperators],
    type: 'text',
    source: 'signal',
  },
  {
    label: 'Entity Value',
    prop: 'entity.value',
    operators: [...globalOperators, ...stringOperators],
    query: (value) =>
      autocompleteQuery(
        SearchResultType.Entities,
        'value',
        value,
        graphqlContext
      ),
    type: 'text',
    source: 'signal',
  },
  {
    label: 'Entity Type',
    prop: 'entity.type',
    operators: [...globalOperators],
    type: 'list',
    source: 'signal',
    options: getEntityTypeFilters(),
  },
  {
    label: 'Entity Sensor Zone',
    prop: 'entity.sensorZone',
    query: (value) =>
      autocompleteQuery(
        SearchResultType.Entities,
        'sensorZone',
        value,
        graphqlContext
      ),
    operators: [...globalOperators],
    type: 'text',
    source: 'signal',
  },
  {
    label: 'Vendor',
    prop: 'vendor',
    query: (value) =>
      autocompleteQuery(
        SearchResultType.Signals,
        'vendor',
        value,
        graphqlContext
      ),
    operators: [...globalOperators, ...stringOperators],
    type: 'text',
    source: 'signal',
  },
  {
    label: 'Product',
    prop: 'product',
    query: (value) =>
      autocompleteQuery(
        SearchResultType.Signals,
        'product',
        value,
        graphqlContext
      ),
    operators: [...globalOperators, ...stringOperators],
    type: 'text',
    source: 'signal',
  },
  ...(canSearchCanaries
    ? [
        {
          label: 'Canaries',
          prop: 'canary',
          operators: [
            {
              label: 'should be',
              type: 'equals',
            },
          ],
          type: 'list',
          source: 'signal',
          options: [
            {
              label: 'Excluded (default)',
              prop: 'exclude',
            },
            {
              label: 'Included (along with non-canaries)',
              prop: 'include',
            },
            {
              label: 'Included (without non-canaries)',
              prop: 'only',
            },
          ],
        } as FieldTypes,
      ]
    : []),
];
